<script setup lang="ts">
import { EMPTY_TOOLTIP } from "~/modules/dtwin/constants";
import { DtwinConnectivityStatus } from "~/modules/dtwin/models/DtwinConnectivityStatus.enum";
import type { DtwinConnectivityStatusParameters } from "~/modules/dtwin/models/DtwinConnectivityStatusParameters.interface";
import { RoundMode } from "~/modules/dtwin/utils/dtwinsListConnectivityStatus";
import { getDuration } from "~/modules/dtwin/utils/getDuration";
import ILxStepError from "~icons/lxc-business/step-error";
import ILxStepSuccess from "~icons/lxc-business/step-success";
import ILxStepWarning from "~icons/lxc-business/step-warning";

const { t } = useI18n();

const props = defineProps<{
  connectivityStatus?: DtwinConnectivityStatus | undefined;
  connectivityStatusParameters?: DtwinConnectivityStatusParameters | undefined;
  center?: boolean | undefined;
}>();

const tooltip: ComputedRef<string> = computed(() => {
  let tooltip = EMPTY_TOOLTIP;
  if (props.connectivityStatus !== undefined) {
    switch (props.connectivityStatus) {
      case DtwinConnectivityStatus.GOOD:
        if (
          props.connectivityStatusParameters?.durationLimitBeforeWarn !==
          undefined
        ) {
          const duration: string | null = getDuration(
            props.connectivityStatusParameters.durationLimitBeforeWarn,
            RoundMode.ROUND_UP,
            t,
          );
          if (duration != null) {
            tooltip = `${t("dtwins.list.attributes.connectivity.connected.lessThan", { duration })}`;
          }
        }
        break;
      case DtwinConnectivityStatus.WARN:
        if (
          props.connectivityStatusParameters?.durationLimitBeforeWarn !==
          undefined
        ) {
          const duration: string | null = getDuration(
            props.connectivityStatusParameters.durationLimitBeforeWarn,
            RoundMode.ROUND_DOWN,
            t,
          );
          if (duration != null) {
            tooltip = `${t("dtwins.list.attributes.connectivity.connected.greaterThan", { duration })}`;
          }
        }
        break;
      case DtwinConnectivityStatus.ERROR:
        if (
          props.connectivityStatusParameters?.durationLimitBeforeError !==
          undefined
        ) {
          const duration: string | null = getDuration(
            props.connectivityStatusParameters.durationLimitBeforeError,
            RoundMode.ROUND_DOWN,
            t,
          );
          if (duration != null) {
            tooltip = `${t("dtwins.list.attributes.connectivity.connected.greaterThan", { duration })}`;
          }
        }
        break;
      case DtwinConnectivityStatus.UNKNOWN:
      default:
      // Default case, no icon is displayed.
    }
  }
  return tooltip;
});

const spanClass: ComputedRef<string[]> = computed(() => {
  let result: string[] = ["block", "w-fit"];
  if (props.center) {
    result.push("mx-auto");
  }
  return result;
});
</script>

<template>
  <span :title="tooltip" :class="spanClass">
    <i-lx-step-success
      v-if="
        connectivityStatus == DtwinConnectivityStatus.GOOD ||
        connectivityStatus == DtwinConnectivityStatus.ONLINE
      "
    />
    <i-lx-step-warning
      v-if="connectivityStatus == DtwinConnectivityStatus.WARN"
    />
    <i-lx-step-error
      v-if="
        connectivityStatus == DtwinConnectivityStatus.ERROR ||
        connectivityStatus == DtwinConnectivityStatus.OFFLINE
      "
    />
  </span>
</template>
